import { useMemo, useState } from 'react';
import { trpc } from '../lib/trpc';
import { useAsync } from '../lib/useAsync';
import { Loading, ErrorBox, EmptyBox, PageTitle } from '../components/admin/ui';

interface Etapa {
  id: string;
  ordem: number;
  titulo: string;
  descricao: string | null;
  status: 'prevista' | 'em_andamento' | 'concluida';
  dataPrevista: string | null;
  dataConcluida: string | null;
  notificadaEm: Date | string | null;
}

/**
 * Tela /obras (incorporadora): cronograma de cada empreendimento em etapas.
 * Os empreendimentos aparecem de duas formas: DERIVADOS do nome no prefixo da
 * unidade ("Mirante · 701" → "Mirante") na importação, ou CRIADOS à mão aqui.
 * A plataforma sugere o cronograma padrão; a incorporadora edita e marca o
 * andamento. Concluir uma etapa avisa os compradores do empreendimento por
 * e-mail (uma única vez).
 */
export function ObrasPage() {
  const { data: obras, loading, error, reload } = useAsync(() => trpc.obra.list.query());
  const [painel, setPainel] = useState<{ id: string; tipo: 'etapas' | 'associar' } | null>(null);
  const [criando, setCriando] = useState(false);
  const [nome, setNome] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  function abrir(id: string, tipo: 'etapas' | 'associar') {
    setPainel((p) => (p && p.id === id && p.tipo === tipo ? null : { id, tipo }));
  }

  async function criarObra() {
    const n = nome.trim();
    if (!n) return;
    setBusy(true);
    try {
      const r = await trpc.obra.criar.mutate({ nome: n });
      setNome('');
      setCriando(false);
      setMsg(
        r.jaExistia
          ? `"${r.nome}" já existia — abra para associar contratos.`
          : `Empreendimento "${r.nome}" criado.`,
      );
      reload();
    } finally {
      setBusy(false);
    }
  }

  async function detectar() {
    setBusy(true);
    try {
      const r = await trpc.obra.reprocessarDerivacao.mutate();
      setMsg(
        r.ligados > 0
          ? `${r.ligados} ${r.ligados === 1 ? 'contrato ligado' : 'contratos ligados'} a ${r.empreendimentos} ${r.empreendimentos === 1 ? 'obra' : 'obras'} pelas unidades.`
          : 'Nenhuma unidade nova com o padrão "Obra · número" para detectar.',
      );
      reload();
    } finally {
      setBusy(false);
    }
  }

  const acoes = (
    <>
      <button
        type="button"
        disabled={busy}
        onClick={() => setCriando(true)}
        className="btn btn-primary disabled:opacity-60"
        data-testid="criar-empreendimento"
      >
        Criar empreendimento
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={detectar}
        className="btn btn-ghost disabled:opacity-60"
        data-testid="detectar-empreendimentos"
      >
        Detectar pelas unidades
      </button>
    </>
  );

  return (
    <div>
      <PageTitle titulo="Obras" />
      <p className="-mt-4 mb-6 max-w-2xl text-sm text-ink-3">
        O cronograma que o comprador acompanha no aplicativo. Marcar uma etapa como concluída avisa os
        compradores do empreendimento por e-mail, uma única vez.
      </p>

      {msg && (
        <div
          className="mb-4 rounded-xl px-3.5 py-2.5 text-[13px]"
          style={{ background: 'var(--accent-soft)', color: 'var(--accent-deep)' }}
          data-testid="obra-msg"
        >
          {msg}
        </div>
      )}

      {obras && obras.length > 0 && <div className="mb-4 flex flex-wrap items-center gap-3">{acoes}</div>}

      {criando && (
        <div className="card mb-4 p-5" data-testid="form-criar-empreendimento">
          <label className="block text-sm font-medium text-ink">Nome do empreendimento</label>
          <div className="mt-2 flex flex-wrap gap-2">
            <input
              autoFocus
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && criarObra()}
              placeholder="Ex.: Residencial Mirante"
              className="min-w-0 flex-1 rounded-xl border border-line-2 px-3.5 py-2.5 text-sm text-ink outline-none focus:border-accent"
              data-testid="nome-empreendimento"
            />
            <button
              type="button"
              disabled={busy || !nome.trim()}
              onClick={criarObra}
              className="btn btn-primary disabled:opacity-60"
              data-testid="salvar-empreendimento"
            >
              Salvar
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setCriando(false);
                setNome('');
              }}
              className="btn btn-ghost disabled:opacity-60"
            >
              Cancelar
            </button>
          </div>
          <p className="mt-2 text-xs text-ink-3">
            Depois de criar, use "Associar contratos" no card para incluir as unidades desta obra.
          </p>
        </div>
      )}

      {loading && <Loading />}
      {error && <ErrorBox msg={error} onRetry={reload} />}

      {obras && obras.length === 0 && !criando && (
        <EmptyBox
          titulo="Nenhum empreendimento ainda"
          descricao={
            'Os empreendimentos vêm da planilha (nome da obra em cada contrato ou no formato "Obra · unidade") ou você cria um aqui e associa os contratos. Depois, marque etapas concluídas para avisar os compradores.'
          }
        >
          {acoes}
        </EmptyBox>
      )}

      {obras && obras.length > 0 && (
        <div className="space-y-3">
          {obras.map((o) => (
            <div key={o.id} className="card p-5" data-testid="obra-empreendimento">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-[16px] font-semibold text-ink">{o.nome}</div>
                  <div className="mt-0.5 text-xs text-ink-3">
                    {o.contratos} {o.contratos === 1 ? 'contrato' : 'contratos'}
                    {o.dataPrevistaEntrega ? ` · entrega prevista ${o.dataPrevistaEntrega}` : ''}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => abrir(o.id, 'associar')}
                    className="btn btn-ghost"
                    data-testid="obra-associar"
                  >
                    {painel?.id === o.id && painel.tipo === 'associar' ? 'Fechar' : 'Associar contratos'}
                  </button>
                  <button
                    type="button"
                    onClick={() => abrir(o.id, 'etapas')}
                    className="btn btn-ghost"
                    data-testid="obra-abrir"
                  >
                    {painel?.id === o.id && painel.tipo === 'etapas'
                      ? 'Fechar'
                      : o.etapas > 0
                        ? 'Editar etapas'
                        : 'Criar cronograma'}
                  </button>
                </div>
              </div>
              {painel?.id === o.id && painel.tipo === 'etapas' && <EtapasEditor empreendimentoId={o.id} />}
              {painel?.id === o.id && painel.tipo === 'associar' && (
                <AssociarPanel
                  empreendimentoId={o.id}
                  nome={o.nome}
                  onDone={(texto) => {
                    setMsg(texto);
                    reload();
                  }}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface ContratoAssociavel {
  id: string;
  unidade: string;
  codigo: string | null;
  compradorNome: string;
  empreendimentoId: string | null;
  empreendimentoNome: string | null;
}

/**
 * Painel de associação: em massa por prefixo da unidade ("unidades que começam
 * com…") e individual por seleção. Reatribuir um contrato de outra obra é
 * permitido — é a forma de corrigir o agrupamento.
 */
function AssociarPanel({
  empreendimentoId,
  nome,
  onDone,
}: {
  empreendimentoId: string;
  nome: string;
  onDone: (msg: string) => void;
}) {
  const { data, loading, error, reload } = useAsync<ContratoAssociavel[]>(
    () => trpc.obra.contratosParaAssociar.query(),
    [empreendimentoId],
  );
  const [prefixo, setPrefixo] = useState('');
  const [filtro, setFiltro] = useState('');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const filtrados = useMemo(() => {
    const f = filtro.trim().toLowerCase();
    const arr = data ?? [];
    if (!f) return arr;
    return arr.filter(
      (c) =>
        c.unidade.toLowerCase().includes(f) ||
        c.compradorNome.toLowerCase().includes(f) ||
        (c.codigo ?? '').toLowerCase().includes(f),
    );
  }, [data, filtro]);
  const visiveis = filtrados.slice(0, 200);

  function toggle(id: string) {
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  async function porPrefixo() {
    const p = prefixo.trim();
    if (!p) return;
    setBusy(true);
    try {
      const r = await trpc.obra.associarPorPrefixo.mutate({ empreendimentoId, prefixo: p });
      setPrefixo('');
      onDone(
        r.associados > 0
          ? `${r.associados} ${r.associados === 1 ? 'contrato associado' : 'contratos associados'} a "${nome}".`
          : `Nenhum contrato com unidade começando por "${p}".`,
      );
      reload();
    } finally {
      setBusy(false);
    }
  }

  async function porSelecao() {
    if (sel.size === 0) return;
    setBusy(true);
    try {
      const ids = [...sel];
      const r = await trpc.obra.associarContratos.mutate({ empreendimentoId, contratoIds: ids });
      setSel(new Set());
      onDone(`${r.associados} ${r.associados === 1 ? 'contrato associado' : 'contratos associados'} a "${nome}".`);
      reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 rounded-xl border border-line-2 p-4" data-testid="obra-associar-panel">
      {/* Em massa por prefixo */}
      <div className="text-sm font-medium text-ink">Associar em massa</div>
      <p className="mt-1 text-xs text-ink-3">
        Inclui todos os contratos cuja unidade começa com o texto abaixo (ex.: "{nome}").
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <input
          value={prefixo}
          onChange={(e) => setPrefixo(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && porPrefixo()}
          placeholder="Unidade começa com…"
          className="min-w-0 flex-1 rounded-xl border border-line-2 px-3.5 py-2.5 text-sm text-ink outline-none focus:border-accent"
          data-testid="associar-prefixo-input"
        />
        <button
          type="button"
          disabled={busy || !prefixo.trim()}
          onClick={porPrefixo}
          className="btn btn-primary disabled:opacity-60"
          data-testid="associar-prefixo-btn"
        >
          Associar
        </button>
      </div>

      {/* Individual */}
      <div className="mt-5 flex items-center justify-between gap-3">
        <div className="text-sm font-medium text-ink">Associar contratos específicos</div>
        {sel.size > 0 && (
          <button
            type="button"
            disabled={busy}
            onClick={porSelecao}
            className="btn btn-primary disabled:opacity-60"
            data-testid="associar-selecao-btn"
          >
            Associar {sel.size} {sel.size === 1 ? 'selecionado' : 'selecionados'}
          </button>
        )}
      </div>
      <input
        value={filtro}
        onChange={(e) => setFiltro(e.target.value)}
        placeholder="Filtrar por unidade, comprador ou código…"
        className="mt-2 w-full rounded-xl border border-line-2 px-3.5 py-2.5 text-sm text-ink outline-none focus:border-accent"
        data-testid="associar-filtro"
      />

      {loading && <div className="mt-3 text-sm text-ink-3">Carregando contratos…</div>}
      {error && <ErrorBox msg={error} onRetry={reload} />}
      {data && data.length === 0 && (
        <div className="mt-3 text-sm text-ink-3">Nenhum contrato importado ainda.</div>
      )}
      {data && data.length > 0 && (
        <>
          <div className="mt-2 max-h-72 space-y-1 overflow-y-auto" data-testid="associar-lista">
            {visiveis.map((c) => {
              const jaNesta = c.empreendimentoId === empreendimentoId;
              return (
                <label
                  key={c.id}
                  className="flex cursor-pointer items-center gap-3 rounded-lg border border-line px-3 py-2 text-sm hover:border-accent"
                >
                  <input
                    type="checkbox"
                    checked={sel.has(c.id)}
                    onChange={() => toggle(c.id)}
                    className="h-4 w-4 shrink-0 accent-[var(--accent)]"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="font-medium text-ink">{c.unidade}</span>
                    <span className="text-ink-3"> · {c.compradorNome}</span>
                  </span>
                  <span className="shrink-0 text-xs text-ink-3">
                    {jaNesta ? 'nesta obra' : c.empreendimentoNome ? `em ${c.empreendimentoNome}` : 'sem obra'}
                  </span>
                </label>
              );
            })}
          </div>
          {filtrados.length > visiveis.length && (
            <div className="mt-2 text-xs text-ink-3">
              Mostrando {visiveis.length} de {filtrados.length}. Refine o filtro ou use a associação em massa.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function EtapasEditor({ empreendimentoId }: { empreendimentoId: string }) {
  const { data: etapas, loading, error, reload } = useAsync(
    () => trpc.obra.etapas.query({ empreendimentoId }),
    [empreendimentoId],
  );
  const [busy, setBusy] = useState(false);
  const [avisados, setAvisados] = useState<string | null>(null);

  async function agir(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      reload();
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Loading label="Carregando etapas…" />;
  if (error) return <ErrorBox msg={error} onRetry={reload} />;

  if (!etapas || etapas.length === 0) {
    return (
      <div className="mt-4 rounded-xl border border-dashed border-line-2 p-5 text-center">
        <p className="text-sm text-ink-3">
          Comece com o modelo sugerido: fundação, estrutura, alvenaria, instalações, acabamento, vistoria e
          entrega das chaves. Você edita tudo depois.
        </p>
        <button
          type="button"
          disabled={busy}
          onClick={() => agir(() => trpc.obra.criarEtapasPadrao.mutate({ empreendimentoId }))}
          className="btn btn-primary mt-4 disabled:opacity-60"
          data-testid="criar-etapas-padrao"
        >
          Criar cronograma sugerido
        </button>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-2">
      {avisados && (
        <div
          className="rounded-xl px-3.5 py-2.5 text-[13px]"
          style={{ background: 'var(--accent-soft)', color: 'var(--accent-deep)' }}
          data-testid="obra-avisados"
        >
          {avisados}
        </div>
      )}
      {(etapas as Etapa[]).map((e) => (
        <div key={e.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-line px-4 py-3" data-testid="obra-etapa-linha">
          <span
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold"
            style={
              e.status === 'concluida'
                ? { background: 'var(--accent)', color: 'white' }
                : e.status === 'em_andamento'
                  ? { background: 'var(--accent-soft)', color: 'var(--accent)' }
                  : { background: 'var(--surface-2)', color: 'var(--ink-3)' }
            }
          >
            {e.ordem}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-ink">{e.titulo}</div>
            <div className="text-xs text-ink-3">
              {e.status === 'concluida'
                ? `concluída${e.dataConcluida ? ` em ${e.dataConcluida}` : ''}${e.notificadaEm ? ' · compradores avisados' : ''}`
                : e.status === 'em_andamento'
                  ? 'em andamento'
                  : e.dataPrevista
                    ? `prevista para ${e.dataPrevista}`
                    : 'prevista'}
            </div>
          </div>
          <div className="flex gap-2">
            {e.status !== 'em_andamento' && e.status !== 'concluida' && (
              <button
                type="button"
                disabled={busy}
                onClick={() => agir(() => trpc.obra.marcarStatus.mutate({ etapaId: e.id, status: 'em_andamento' }))}
                className="rounded-full border border-line-2 px-3 py-1.5 text-xs font-medium text-ink-2 disabled:opacity-60"
                data-testid="etapa-iniciar"
              >
                Começou
              </button>
            )}
            {e.status !== 'concluida' && (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  agir(async () => {
                    const r = await trpc.obra.marcarStatus.mutate({ etapaId: e.id, status: 'concluida' });
                    setAvisados(
                      r.avisados > 0
                        ? `Etapa concluída. ${r.avisados} ${r.avisados === 1 ? 'comprador avisado' : 'compradores avisados'} por e-mail.`
                        : 'Etapa concluída.',
                    );
                  })
                }
                className="rounded-full px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                style={{ background: 'var(--accent)' }}
                data-testid="etapa-concluir"
              >
                Concluída
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
